import {
  BookingAddOns,
  CANCELLATION_FEE_PRODUCT_KEY,
  FREE_CANCELLATION_FEE_PRODUCT_KEY,
  GUEST_DAILY_RATE_PRODUCT_KEY,
  GUEST_HOURLY_RATE_PRODUCT_KEY,
  GUEST_INSURANCE_FEE_PRODUCT_KEY,
  GUEST_PREMIUM_INSURANCE_FEE_PRODUCT_KEY,
  GUEST_SERVICE_FEE_PRODUCT_KEY,
  INSURANCE_PRODUCT_KEY,
  INSURANCE_PRODUCT_KEYS_BY_CONTEXT,
  InsuranceContext,
  InsuranceCoverageTier,
  PREMIUM_INSURANCE_PRODUCT_KEY,
  PREMIUM_INSURANCE_PRODUCT_KEYS,
  Product,
  ProductInfo,
  SAME_DAY_STORAGE_DAY_PRODUCT_KEY,
  SAME_DAY_STORAGE_HOUR_PRODUCT_KEY,
  SERVICE_FEE_PRODUCT_KEY,
  STORAGE_DAY_PRODUCT_KEY,
  STORAGE_HOUR_PRODUCT_KEY,
  TIP_PRODUCT_KEY,
  WALK_IN_DAILY_RATE_PRODUCT_KEY,
  WALK_IN_HOURLY_RATE_PRODUCT_KEY,
  WALK_IN_INSURANCE_FEE_PRODUCT_KEY,
  WALK_IN_PREMIUM_INSURANCE_FEE_PRODUCT_KEY,
  WALK_IN_SERVICE_FEE_PRODUCT_KEY,
} from '@luggagehero/shared/interfaces';

//
// Lookup tables for product keys
//
const SERVICE_FEE_PRODUCT_KEYS = [
  SERVICE_FEE_PRODUCT_KEY,
  WALK_IN_SERVICE_FEE_PRODUCT_KEY,
  GUEST_SERVICE_FEE_PRODUCT_KEY,
];
const HOURLY_STORAGE_FEE_PRODUCT_KEYS = [
  WALK_IN_HOURLY_RATE_PRODUCT_KEY,
  GUEST_HOURLY_RATE_PRODUCT_KEY,
  SAME_DAY_STORAGE_HOUR_PRODUCT_KEY,
  STORAGE_HOUR_PRODUCT_KEY,
];
const DAILY_STORAGE_FEE_PRODUCT_KEYS = [
  WALK_IN_DAILY_RATE_PRODUCT_KEY,
  GUEST_DAILY_RATE_PRODUCT_KEY,
  SAME_DAY_STORAGE_DAY_PRODUCT_KEY,
  STORAGE_DAY_PRODUCT_KEY,
];
const STORAGE_FEE_PRODUCT_KEYS = [...HOURLY_STORAGE_FEE_PRODUCT_KEYS, ...DAILY_STORAGE_FEE_PRODUCT_KEYS];
const INSURANCE_FEE_PRODUCT_KEYS = [
  INSURANCE_PRODUCT_KEY,
  WALK_IN_INSURANCE_FEE_PRODUCT_KEY,
  WALK_IN_PREMIUM_INSURANCE_FEE_PRODUCT_KEY,
  GUEST_INSURANCE_FEE_PRODUCT_KEY,
  GUEST_PREMIUM_INSURANCE_FEE_PRODUCT_KEY,
  PREMIUM_INSURANCE_PRODUCT_KEY,
];
const CANCELLATION_FEE_PRODUCT_KEYS = [CANCELLATION_FEE_PRODUCT_KEY, FREE_CANCELLATION_FEE_PRODUCT_KEY];
const TIP_PRODUCT_KEYS = [TIP_PRODUCT_KEY];

const isProductInfo = (value: Product | ProductInfo): value is ProductInfo => {
  return typeof (value as ProductInfo).key === 'string';
};

export abstract class SharedUtilProduct {
  public static getProductKey(value: Product | ProductInfo): string {
    return isProductInfo(value) ? value.key : value._id || '';
  }

  public static isServiceFee(value: Product | ProductInfo): boolean {
    if (!value) {
      return false;
    }
    return SERVICE_FEE_PRODUCT_KEYS.includes(this.getProductKey(value));
  }

  public static isStorageFee(value: Product | ProductInfo): boolean {
    if (!value) {
      return false;
    }
    return STORAGE_FEE_PRODUCT_KEYS.includes(this.getProductKey(value));
  }

  public static isHourlyStorageFee(value: Product | ProductInfo): boolean {
    if (!value) {
      return false;
    }
    return HOURLY_STORAGE_FEE_PRODUCT_KEYS.includes(this.getProductKey(value));
  }

  public static isDailyStorageFee(value: Product | ProductInfo): boolean {
    if (!value) {
      return false;
    }
    return DAILY_STORAGE_FEE_PRODUCT_KEYS.includes(this.getProductKey(value));
  }

  public static isInsuranceFee(value: Product | ProductInfo): boolean {
    if (!value) {
      return false;
    }
    return INSURANCE_FEE_PRODUCT_KEYS.includes(this.getProductKey(value));
  }

  public static isPremiumInsuranceFee(value: Product | ProductInfo): boolean {
    if (!value) {
      return false;
    }
    return PREMIUM_INSURANCE_PRODUCT_KEYS.includes(this.getProductKey(value));
  }

  /**
   * Which insurance product family prices a booking with these add-ons.
   *
   * `walkIn` wins over `guest` to match the order-summary rendering that predates coverage tiers — a
   * guest booking flagged as a walk-in is priced from the walk-in products.
   */
  public static insuranceContext(addOns: Pick<BookingAddOns, 'walkIn' | 'guest'> | null | undefined): InsuranceContext {
    if (addOns?.walkIn) {
      return 'walkIn';
    }
    if (addOns?.guest) {
      return 'guest';
    }
    return 'preBooking';
  }

  /**
   * The product key that prices `tier` in `context`, or `undefined` for `none` (declined cover — no
   * insurance product goes on the order at all).
   */
  public static insuranceKeyForTier(context: InsuranceContext, tier: InsuranceCoverageTier): string | undefined {
    if (tier === 'none') {
      return undefined;
    }
    return INSURANCE_PRODUCT_KEYS_BY_CONTEXT[context][tier];
  }

  /**
   * The quote's product for `tier` in `context`, but **only if that tier can actually be sold**.
   *
   * A `cost` is required for either tier: a cost of **0** is a real price (some contexts include cover),
   * while `null`/`undefined` means the product exists but this pricing context doesn't price it — the
   * server would put no insurance line on the order at all.
   *
   * A coverage `value` is required for **Premium only**. Premium is an upsell, so offering it without a
   * stated coverage amount would charge more for an unquantified benefit. Basic is the flow's baseline
   * cover and must stay sellable even where its pricing row is missing a coverage amount (10 of 68
   * walk-in basic rows in production are) — the card then renders without the coverage line rather than
   * disappearing and leaving "No coverage" as the traveler's only option.
   *
   * Premium must also **cost at least as much as Basic**. The server refuses to price an inverted pair
   * (`getInsuranceRateKeyFromOrderRequest` falls back to Basic), and a few production contexts — including
   * the `WW` fallback — really are inverted, so without this the traveler would pick a Premium card and be
   * charged Basic, with the summary line then reading "Insurance" at the Basic price underneath it.
   *
   * The single home for that rule: the view uses it to decide what to render, and the store uses it to
   * decide whether a previously chosen tier is still on offer. If they disagreed, the traveler could see
   * one tier and be quoted another — which is also why the rule mirrors the server's, rather than being a
   * looser client-side approximation of it.
   */
  public static findOfferableInsuranceProduct(
    products: ProductInfo[] | undefined,
    context: InsuranceContext,
    tier: 'basic' | 'premium',
  ): ProductInfo | undefined {
    const keys = INSURANCE_PRODUCT_KEYS_BY_CONTEXT[context];
    const product = products?.find((p) => p.key === keys[tier]);
    const isPriced = product && product.cost !== null && product.cost !== undefined;
    if (!isPriced) {
      return undefined;
    }

    if (tier === 'basic') {
      return product;
    }

    if (!product.value) {
      return undefined;
    }

    // Same comparison the server makes, and only when both are priced — an unpriced Basic leaves nothing
    // to compare against.
    const basicCost = products?.find((p) => p.key === keys.basic)?.cost;
    const undercutsBasic = basicCost !== null && basicCost !== undefined && product.cost < basicCost;

    return undercutsBasic ? undefined : product;
  }

  public static isTip(value: Product | ProductInfo): boolean {
    if (!value) {
      return false;
    }
    return TIP_PRODUCT_KEYS.includes(this.getProductKey(value));
  }

  public static isCancellationFee(value: Product | ProductInfo): boolean {
    if (!value) {
      return false;
    }
    return CANCELLATION_FEE_PRODUCT_KEYS.includes(this.getProductKey(value));
  }
}
